import { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createPortal } from "react-dom";
import { X, Edit } from "lucide-react";
import axios from 'axios';
import {
  getPurchaseOrderById,
  PurchaseOrdersManagementLoadingKeys,
} from "../../slices/managementSlices/purchaseOrdersSlice";
import PurchaseOrderSidebar from "../../components/PurchaseOrderSidebar";
import CSVExportButton from "../../components/reports/CSVExportButton";
import { CreateShipping } from "../../components/shipping/CreateShipping";
import { CreateContainerByShipping } from "../../components/shipping/CreateShippingContainer";
import PurchaseOrderEmailComposer from "../../components/PurchaseOrderEmailComposer";
import { addShipmentElement, addContainerToThePO, printAllContainers, printAllPOLabels } from "../../slices/shippingSlice";
import { unwrapResult } from '@reduxjs/toolkit';
import printJS from 'print-js';
import { arrayHasItems, formatCurrencyUsdFixedTwo, formatCurrencyUsdTwoOrFour } from "../../helpers/common";
import { SelectDropdown } from "../../components/SelectDropdown";
import { ChevronDownIcon, EyeIcon } from "@heroicons/react/24/outline";
import { LoaderSpinner } from "../../components/LoaderSpinner";
import PurchaseOrderStatusChip from "./PurchaseOrderStatusChip";
import { ConfrimModal } from "../../components/ConfrimModal";
import { computeCartonMismatches } from "./cartonQty";
export default function PODetailsSidebar({ purchaseOrderId, onClose, initialMode = 'view', onModeChange, onUpdate, onCanonicalIdChange, access }) {
  const dispatch = useDispatch();
  const { purchaseOrderDetails, loading, loadingKeys } = useSelector(
    (state) => state.purchaseOrdersManagement,
  );
  const [vendorInfo, setVendorInfo] = useState(null);
  const [materialSpecsMap, setMaterialSpecsMap] = useState({});
  const [mode, setMode] = useState(initialMode); // 'view' | 'edit'
  const [epcs, setEpcs] = useState([]);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [epcIsLoading, setEpcIsLoading] = useState(false);
  const [epcIsGenerating, setEpcIsGenerating] = useState(false);
  const [isEpcModalOpen, setIsEpcModalOpen] = useState(false);
  const [isCreateShippingOpen, setIsCreateShippingOpen] = useState(false);
  const [isCreateContainerOpen, setIsCreateContainerOpen] = useState(false);
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const [createMenuStyles, setCreateMenuStyles] = useState(null);
  const createMenuButtonRef = useRef(null);
  const createMenuRef = useRef(null);
  const [isEmailComposerOpen, setIsEmailComposerOpen] = useState(false);
  const [hasShipment, setHasShipment] = useState(false);
  const [isCheckingShipment, setIsCheckingShipment] = useState(false);
  const [isPrintingLabels, setIsPrintingLabels] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [isSendConfirmOpen, setIsSendConfirmOpen] = useState(false);
  const [activationError, setActivationError] = useState('');
  const [activePurchaseOrderId, setActivePurchaseOrderId] = useState(purchaseOrderId);
  const [billcomBills, setBillcomBills] = useState([]);
  const [billcomVendorUrl, setBillcomVendorUrl] = useState(null);

  useEffect(() => {
    const updateCreateMenuPosition = () => {
      if (!createMenuButtonRef.current) return;
      const rect = createMenuButtonRef.current.getBoundingClientRect();
      const menuWidth = 180;
      setCreateMenuStyles({
        top: rect.bottom + 4,
        left: Math.min(rect.left, window.innerWidth - menuWidth - 8),
        minWidth: menuWidth,
      });
    };

    const handleCreateMenuOutsideClick = (event) => {
      if (
        createMenuButtonRef.current &&
        !createMenuButtonRef.current.contains(event.target) &&
        !createMenuRef.current?.contains(event.target)
      ) {
        setIsCreateMenuOpen(false);
      }
    };

    if (isCreateMenuOpen) {
      updateCreateMenuPosition();
      document.addEventListener('mousedown', handleCreateMenuOutsideClick);
      document.addEventListener('scroll', updateCreateMenuPosition, true);
      window.addEventListener('resize', updateCreateMenuPosition);
    }

    return () => {
      document.removeEventListener('mousedown', handleCreateMenuOutsideClick);
      document.removeEventListener('scroll', updateCreateMenuPosition, true);
      window.removeEventListener('resize', updateCreateMenuPosition);
    };
  }, [isCreateMenuOpen]);

  useEffect(() => {
    setMode(initialMode);
    setActivePurchaseOrderId(purchaseOrderId);
    setSelectedPdf(null);
  }, [initialMode, purchaseOrderId]);

  useEffect(() => {
    if (activePurchaseOrderId) {
      dispatch(getPurchaseOrderById({ id: activePurchaseOrderId }));
    }
  }, [activePurchaseOrderId, dispatch]);

  useEffect(() => {
    if (!activePurchaseOrderId) return;
    let cancelled = false;
    const checkShipment = async () => {
      try {
        setIsCheckingShipment(true);
        const { data } = await axios.get(`/api/v1/management/purchase-order/${activePurchaseOrderId}/shipment/exists`);
        if (!cancelled) setHasShipment(data?.exists || false);
      } catch (e) {
        if (!cancelled) setHasShipment(false);
      } finally {
        if (!cancelled) setIsCheckingShipment(false);
      }
    };
    checkShipment();
    return () => { cancelled = true; };
  }, [activePurchaseOrderId]);

  useEffect(() => {
    if (!activePurchaseOrderId) {
      setEpcs([]);
      return;
    }
    let cancelled = false;
    const fetchEpcs = async () => {
      try {
        setEpcIsLoading(true);
        const { data } = await axios.get(`/api/v1/management/purchase-order/${activePurchaseOrderId}/epcs`);
        if (!cancelled) setEpcs(data?.items || []);
      } catch (e) {
        if (!cancelled) setEpcs([]);
      } finally {
        if (!cancelled) setEpcIsLoading(false);
      }
    };
    fetchEpcs();
    return () => { cancelled = true; };
  }, [activePurchaseOrderId]);

  useEffect(() => {
    if (!activePurchaseOrderId) {
      setBillcomBills([]);
      setBillcomVendorUrl(null);
      return;
    }
    let cancelled = false;
    axios.get(`/api/v1/purchase-orders/${activePurchaseOrderId}/billcom-bills`)
      .then(({ data }) => {
        if (cancelled) return;
        setBillcomBills(data?.billcom_bills || []);
        setBillcomVendorUrl(data?.billcom_vendor_url || null);
      })
      .catch(() => {
        if (!cancelled) {
          setBillcomBills([]);
          setBillcomVendorUrl(null);
        }
      });
    return () => { cancelled = true; };
  }, [activePurchaseOrderId]);

  useEffect(() => {
    if (!purchaseOrderDetails?.vendor) {
      setVendorInfo(null);
      return;
    }
    axios
      .get('/api/v1/management/vendor', { params: { id: purchaseOrderDetails.vendor } })
      .then(({ data }) => setVendorInfo(data.vendor))
      .catch(() => setVendorInfo(null));
  }, [purchaseOrderDetails?.vendor]);

  useEffect(() => {
    if (!purchaseOrderDetails?.po_line_items) {
      setMaterialSpecsMap({});
      return;
    }
    setMaterialSpecsMap({});
    purchaseOrderDetails.po_line_items.forEach(li => {
      axios.get(`/api/v1/materials/${li.id}`)
        .then(({ data }) => {
          setMaterialSpecsMap(prev => ({ ...prev, [li.id]: data?.data || [] }));
        })
        .catch(() => {
          setMaterialSpecsMap(prev => ({ ...prev, [li.id]: [] }));
        });
    });
  }, [purchaseOrderDetails?.po_line_items]);

  const isLoading =
    loading &&
    loadingKeys.includes(PurchaseOrdersManagementLoadingKeys.getPurchaseOrderById);

  const po = purchaseOrderDetails;
  const cartonMismatches = computeCartonMismatches(po?.po_line_items);

  const calculateTotal = () => {
    if (!po) return 0;
    const subtotal = po.po_line_items.reduce(
      (sum, item) => sum + item.quantity * Number(item.itemPrice || 0),
      0,
    );
    const shipping = Number(po.shipping_cost || 0);
    return subtotal + shipping;
  };

  const calculateSubtotal = () => {
    if (!po) return 0;
    return po.po_line_items.reduce(
      (sum, item) => sum + item.quantity * Number(item.itemPrice || 0),
      0,
    );
  };

  const calculateTotalQuantity = () => {
    if (!po) return 0;
    return po.po_line_items.reduce((sum, item) => sum + item.quantity, 0);
  };

  const total = calculateTotal();

  const depositFirstAmount = useMemo(() =>
    ((vendorInfo?.depositFirst || 0) / 100) * total,
  [vendorInfo, total]);

  const depositSecondAmount = useMemo(() =>
    ((vendorInfo?.depositSecond || 0) / 100) * total,
  [vendorInfo, total]);

  const balanceAmount = useMemo(() =>
    ((vendorInfo?.balance || 0) / 100) * total,
  [vendorInfo, total]);

  const creditAmount = useMemo(() =>
    ((vendorInfo?.credit || 0) / 100) * total,
  [vendorInfo, total]);

  const paymentBreakdownTotal = useMemo(
    () => depositFirstAmount + depositSecondAmount + balanceAmount + creditAmount,
    [depositFirstAmount, depositSecondAmount, balanceAmount, creditAmount]
  );

  const handleClose = () => {
    if (mode === 'edit') {
      setMode('view');
      onModeChange?.('view');
    } else {
      onClose();
    }
  };

  const handleSend = async () => {
    if (!activePurchaseOrderId || isActivating) return;
    try {
      setIsActivating(true);
      setActivationError('');
      const { data } = await axios.post(`/api/v1/management/purchase-order/${activePurchaseOrderId}/activate`);
      setActivePurchaseOrderId(data.id);
      onCanonicalIdChange?.(data.id, data.poNumber, data.url);
      onUpdate?.();
      setIsSendConfirmOpen(false);
    } catch (error) {
      setActivationError(error?.response?.data?.error || error?.message || 'Unable to publish purchase order');
    } finally {
      setIsActivating(false);
    }
  };

  const handleSendClick = () => {
    if (po?.status === 'DRAFT') {
      setActivationError('');
      setIsSendConfirmOpen(true);
      return;
    }
    setIsEmailComposerOpen(true);
  };

  const handleCreateAction = (action) => {
    setIsCreateMenuOpen(false);
    if (action === 'labels') {
      setIsCreateShippingOpen(true);
    } else if (action === 'container') {
      setIsCreateContainerOpen(true);
    } else if (action === 'epcs') {
      handleGenerateEpcs();
    }
  };

  const closeSendConfirmation = () => {
    setIsSendConfirmOpen(false);
    setActivationError('');
  };

  const handleGenerateEpcs = async () => {
    if (!activePurchaseOrderId || epcIsGenerating) return;
    try {
      setEpcIsGenerating(true);
      await axios.post(`/api/v1/management/purchase-order/${activePurchaseOrderId}/epcs/generate`);
      const { data } = await axios.get(`/api/v1/management/purchase-order/${activePurchaseOrderId}/epcs`);
      setEpcs(data?.items || []);
    } catch (e) {
      // no-op UI toast here; could add toast if desired
    } finally {
      setEpcIsGenerating(false);
    }
  };

  const handleSubmitShipment = (poData, poId) => {
    dispatch(addShipmentElement({ poData, poId, search: '', statuses: [], page: 1, itemsPerPage: 100 }));
    setIsCreateShippingOpen(false);
    setHasShipment(true); // Update state after creating shipment
    onUpdate?.(); // Refresh parent component if needed
  };

  const handleSubmitContainer = async ({
    containerName,
    poId,
    productData
  }) => {
    await dispatch(addContainerToThePO({ containerName, poId, productData, search: '', statuses: [], page: 1, itemsPerPage: 100 })).unwrap();
    setIsCreateContainerOpen(false);
    setHasShipment(true);
    onUpdate?.();
  };
  
  const handlePrintPdf = async () => {
    if (!selectedPdf || isPdfLoading) return;
    if (selectedPdf === 'epcs') {
      setIsEpcModalOpen(true);
      return;
    }
    setIsPdfLoading(true);
    if (selectedPdf === 'shippingLabels') {

      // At the moment downloading the shipping labels can confuse the user so we need to print the labels directly
      // try {
      //   const url = `/api/v1/public/purchase-order/${activePurchaseOrderId}/shipping-labels/download`;
      //   printJS({
      //     printable: url,
      //     type: 'pdf',
      //     multiple: true,
      //     onLoadingEnd: () => {
      //       setIsPdfLoading(false);
      //     }
      //   });
      // } catch (e) {
      //   console.error('Failed to print existed shipping labels:', e);
      //   await handlePrintLabels();
      // } finally {
      //   setIsPdfLoading(false);
      // }

      await handlePrintLabels(true);
      setIsPdfLoading(false);
      return;
    }
    if (selectedPdf === 'po') {
      const url = `/api/v1/public/purchase-order/${activePurchaseOrderId}/po/download`;
      printJS({
        printable: url,
        type: 'pdf',
        multiple: true,
        onLoadingEnd: () => {
          setIsPdfLoading(false);
        }
      });
      return;
    } else if (selectedPdf === 'barcode') {
      try {
        const { data } = await axios.post(`/api/v1/management/purchase-order/${activePurchaseOrderId}/barcodes/generate`);
        const newTab = window.open(data?.url, '_blank', 'noopener,noreferrer');
        if (newTab) newTab.focus();
      } catch (e) {
        console.error('Failed to generate barcodes:', e);
      }
      setIsPdfLoading(false);
      return;
    }
  }

  const handlePrintLabels = async (printDirectly = false) => {
    if (!activePurchaseOrderId || isPrintingLabels) return;
    try {
      if (!printDirectly) {
        setIsPrintingLabels(true);
      }
      // Fetch boxes to build shipmentBoxes structure
      const { data: {
        boxes: boxesData,
        containers: containersData
      } } = await axios.get(`/api/v1/boxes`, {
        params: { po_id: activePurchaseOrderId }
      });

      if (arrayHasItems(containersData)) {
        const resultAction = await dispatch(printAllContainers({ shipmentId: activePurchaseOrderId, shipmentContainers: containersData.map((container) => container.id) }));
        const res = unwrapResult(resultAction);
        printJS({
          printable: res.url,
          type: 'pdf',
          multiple: true
        });
        return;
      }
       
      // Build shipmentBoxes structure: { productId: { boxId: boxIdString } }
      const shipmentBoxes = {};
      let boxIdCounter = 1;
      
      // Group boxes by product_id
      const boxesByProduct = {};
      if (boxesData && Array.isArray(boxesData)) {
        boxesData.forEach(box => {
          if (!boxesByProduct[box.product_id]) {
            boxesByProduct[box.product_id] = [];
          }
          boxesByProduct[box.product_id].push(box);
        });
      }
      
      // Build the structure
      Object.keys(boxesByProduct).forEach(productId => {
        shipmentBoxes[productId] = {};
        boxesByProduct[productId].forEach(box => {
          shipmentBoxes[productId][box.id] = boxIdCounter.toString();
          boxIdCounter++;
        });
      });

      const resultAction = await dispatch(printAllPOLabels({ 
        id: activePurchaseOrderId,
        shipmentBoxes 
      }));
      
      const res = unwrapResult(resultAction);
      printJS({
        printable: res.url,
        type: 'pdf',
        multiple: true
      });
    } catch (e) {
      console.error('Failed to print labels:', e);
      // Could show error toast here
    } finally {
      if (!printDirectly) {
        setIsPrintingLabels(false);
      }
    }
  };

  const epcExportColumns = [
    { key: 'serial', label: 'Serial' },
    { key: 'epc', label: 'EPC' },
    { key: 'sku', label: 'SKU' },
    { key: 'barcode', label: 'Barcode' },
    { key: 'poNumber', label: 'PO#' }
  ];

  const createOptions = po && mode !== 'edit'
    ? [
      po.status !== 'DRAFT' && !hasShipment ? { label: 'Create Labels', value: 'labels', disabled: isCheckingShipment } : null,
      po.status !== 'DRAFT' && !hasShipment ? { label: 'Create Container', value: 'container', disabled: isCheckingShipment } : null,
      po.status !== 'DRAFT' && !epcs.length
        ? {
          label: epcIsLoading ? 'Loading EPCs...' : epcIsGenerating ? 'Generating EPCs...' : 'Generate EPCs',
          value: 'epcs',
          disabled: epcIsLoading || epcIsGenerating,
        }
        : null,
    ].filter(Boolean)
    : [];

  if (mode === 'edit') {
    return (
      <PurchaseOrderSidebar
        purchaseOrderId={activePurchaseOrderId}
        variant="edit"
        isOpen={true}
        embedded
        onClose={handleClose}
        onSuccessSubmit={() => {
          setMode('view');
          onModeChange?.('view');
          dispatch(getPurchaseOrderById({ id: activePurchaseOrderId }));
          onUpdate?.();
        }}
      />
    );
  }

  if (isLoading || !po) {
    return (
      <div className="p-6">Loading...</div>
    );
  }

  return (
    <div className="w-full h-full overflow-y-auto bg-white">
      <div className="border-b p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <h1 className="truncate text-3xl font-bold">{po.poNumber}</h1>
            <PurchaseOrderStatusChip status={po.status} />
            {access === "edit" && (
              <button className="h-8 w-8 shrink-0" onClick={() => { setMode('edit'); onModeChange?.('edit'); }} type="button">
                <Edit className="h-5 w-5" />
              </button>
            )}
          </div>
          <button className="h-8 w-8 shrink-0" onClick={onClose} type="button">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">{po.vendorName}</p>
        <div className="mt-4 space-y-1 text-sm">
          <p>{new Date(po.poDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
          <p>{calculateTotalQuantity().toLocaleString()} units</p>
          <p>{formatCurrencyUsdFixedTwo(total)}</p>
        </div>
        <div className="mt-5 flex w-full min-w-0 flex-wrap items-center gap-2 max-[639px]:max-w-full max-[639px]:flex-nowrap max-[639px]:gap-1 max-[639px]:overflow-x-auto max-[639px]:[scrollbar-width:none] text-sm">
          <span className="max-[639px]:hidden text-sm text-gray-500">View:</span>
          <div className="w-40 max-[639px]:w-[124px] max-[639px]:shrink-0">
            <SelectDropdown
              options={[
                { label: 'Purchase Order', value: 'po' },
                po.status !== 'DRAFT' && hasShipment ? { label: 'Shipping Labels', value: 'shippingLabels' } : null,
                po.status !== 'DRAFT' ? { label: 'Barcode', value: 'barcode' } : null,
                mode !== 'edit' && epcs.length > 0 ? { label: 'EPCs', value: 'epcs' } : null,
              ].filter(Boolean)}
              value={selectedPdf}
              onChange={setSelectedPdf}
              placeholder="Select View"
              portal
              selectClassname="!h-8 !rounded-md !bg-white !px-3 !py-1 !text-sm !text-primary"
              selectedValueClassname="!truncate !text-primary"
            />
          </div>
          <button disabled={!selectedPdf || isPdfLoading} className="relative flex h-8 items-center gap-2 whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-0 text-sm leading-5 text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 max-[639px]:px-2" onClick={handlePrintPdf}>
            <EyeIcon className="h-5 w-5" />
            <span>Show</span>
            {isPdfLoading && (
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <LoaderSpinner width="20" height="20" border="3" colorSpinner="blue" />
              </div>
            )}
          </button>
          {createOptions.length > 0 && (
            <>
              <button
                ref={createMenuButtonRef}
                type="button"
                className="flex h-8 shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-0 text-sm leading-5 text-gray-800 hover:bg-gray-50"
                onClick={() => setIsCreateMenuOpen((open) => !open)}
                aria-expanded={isCreateMenuOpen}
              >
                Create
                <ChevronDownIcon className={`h-4 w-4 transition-transform ${isCreateMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {isCreateMenuOpen && createMenuStyles && createPortal(
                <div
                  ref={createMenuRef}
                  style={{ ...createMenuStyles, position: 'fixed' }}
                  className="z-[120] rounded-md border border-gray-200 bg-white p-1 shadow-lg"
                >
                  {createOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      disabled={option.disabled}
                      className="block w-full whitespace-nowrap rounded px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={() => handleCreateAction(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>,
                document.body
              )}
            </>
          )}
          {mode !== 'edit' && (
            <button type="button" className="inline-flex h-8 shrink-0 items-center whitespace-nowrap rounded-md border border-green-200 bg-green-50 px-3 py-0 text-sm leading-5 text-green-900 hover:bg-green-100 disabled:opacity-50 max-[639px]:px-2" onClick={handleSendClick} disabled={isActivating}>
              {po.status === 'DRAFT' ? 'Publish' : 'Send'}
            </button>
          )}
          {mode !== 'edit' && epcs.length > 0 && (
            <div className="ml-auto shrink-0 whitespace-nowrap">
              <CSVExportButton
                data={epcs}
                columns={epcExportColumns}
                filename={`po_${po.poNumber}_epcs`}
                variant="outline"
                iconOnlyOnMobile
                className="!px-3 !py-1.5 !text-sm"
              />
            </div>
          )}
        </div>
      </div>
      <div className="p-6 text-base">

        <div className="mb-8">
          <h2 className="text-lg font-bold mb-4">Order Summary</h2>
          <div className="space-y-3 border-b border-gray-200 pb-5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Subtotal</span>
              <span className="text-base font-medium">{formatCurrencyUsdFixedTwo(calculateSubtotal())}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Shipping</span>
              <span className="text-base font-medium">{formatCurrencyUsdFixedTwo(po.shipping_cost || 0)}</span>
            </div>
            <div className="flex items-center justify-between border-t-2 border-gray-400 pt-3">
              <span className="text-sm font-medium text-gray-900">Total</span>
              <span className="text-base font-bold">{formatCurrencyUsdFixedTwo(total)}</span>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-lg font-bold mb-4">Line Items</h2>
          <table className="min-w-full text-base">
            <thead>
              <tr className="text-left">
                <th className="w-[140px] text-sm font-medium text-muted-foreground">SKU</th>
                <th className="w-[400px] text-sm font-medium text-muted-foreground">PRODUCT</th>
                <th className="w-[100px] text-right text-sm font-medium text-muted-foreground">QTY</th>
                <th className="w-[100px] text-right text-sm font-medium text-muted-foreground">RCVD</th>
                <th className="w-40 text-right text-sm font-medium text-muted-foreground">UNIT COST</th>
              </tr>
            </thead>
            <tbody>
              {po.po_line_items.map((item) => {
                const materials = materialSpecsMap[item.id] || [];
                return (
                  <tr key={item.id} className="border-t">
                    <td className="py-3 w-[140px]">{item.sku}</td>
                    <td className="py-3 w-[400px]">
                      <div className="flex flex-col">
                        <span>{item.name}</span>
                        {materials.length > 0 && (
                          <div className="mt-2 border-l-4 border-gray-300 bg-gray-50 px-3 py-2">
                            <div className="text-sm font-medium tracking-wide text-gray-500">MATERIAL SPECS</div>
                            {materials.map((m, idx) => (
                              <div key={idx} className="mt-1 text-sm text-gray-500 whitespace-pre-line">
                                {m.description}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 text-right w-[100px]">{item.quantity.toLocaleString()}</td>
                    <td className="py-3 text-right w-[100px]">{item.quantityReceived.toLocaleString()}</td>
                    <td className="py-3 text-right w-40">{formatCurrencyUsdTwoOrFour(item.itemPrice)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {vendorInfo && (
          <div className="mb-8 border-t border-gray-300 pt-5">
            <h2 className="text-lg font-bold mb-4">Payment Breakdown</h2>
            <div>
              <div className="flex justify-between items-center border-b border-gray-200 py-3">
                <span className="text-sm font-medium text-gray-700">Deposit 1 ({vendorInfo.depositFirst || 0}%)</span>
                <span className="text-sm">{formatCurrencyUsdFixedTwo(depositFirstAmount)}</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 py-3">
                <span className="text-sm font-medium text-gray-700">Deposit 2 ({vendorInfo.depositSecond || 0}%)</span>
                <span className="text-sm">{formatCurrencyUsdFixedTwo(depositSecondAmount)}</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 py-3">
                <span className="text-sm font-medium text-gray-700">Balance ({vendorInfo.balance || 0}%)</span>
                <span className="text-sm">{formatCurrencyUsdFixedTwo(balanceAmount)}</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 py-3">
                <span className="text-sm font-medium text-gray-700">Credit ({vendorInfo.credit || 0}%)</span>
                <span className="text-sm">{formatCurrencyUsdFixedTwo(creditAmount)}</span>
              </div>
              <div className="flex justify-between items-center border-t-2 border-gray-400 pt-3 mt-1">
                <span className="text-sm font-medium text-gray-900">Total</span>
                <span className="text-sm font-bold">{formatCurrencyUsdFixedTwo(paymentBreakdownTotal)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="mb-8 border-t border-gray-300 pt-5">
          <h2 className="text-lg font-bold mb-4">Bill.com</h2>
          {billcomBills.length === 0 ? (
            <p className="text-sm text-gray-500">No matching Bill.com bills.</p>
          ) : (
            <div className="space-y-2">
              {billcomBills.map((bill) => (
                <div key={bill.bill_id || bill.invoice_number} className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-2 text-sm md:grid-cols-6">
                  <a href={bill.url || '#'} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                    {bill.invoice_number || bill.bill_id || 'Bill'}
                  </a>
                  <span>Amount: {formatCurrencyUsdFixedTwo(bill.amount || 0)}</span>
                  <span>Paid: {formatCurrencyUsdFixedTwo(bill.paid || 0)}</span>
                  <span>Scheduled: {formatCurrencyUsdFixedTwo(bill.scheduled || 0)}</span>
                  <span>Due: {formatCurrencyUsdFixedTwo(bill.due || 0)}</span>
                  <span>{bill.payment_status || '—'}</span>
                </div>
              ))}
              {billcomVendorUrl && (
                <a href={billcomVendorUrl} target="_blank" rel="noopener noreferrer" className="inline-block text-sm text-blue-700 hover:underline">
                  View Bill.com vendor
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {isEpcModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center w-full h-full overflow-y-auto">
          <div className="fixed inset-0 bg-gray-700 opacity-30" onClick={() => setIsEpcModalOpen(false)} />
          <div className="relative z-50 bg-white border rounded-md shadow-lg max-w-5xl w-[90vw] max-h-[90vh] overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">EPCs for {po.poNumber}</h3>
              <button className="h-8 w-8" onClick={() => setIsEpcModalOpen(false)} type="button">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left">
                    <th className="font-medium text-muted-foreground w-[140px]">SKU</th>
                    <th className="font-medium text-muted-foreground w-[160px]">Barcode</th>
                    <th className="font-medium text-muted-foreground">EPC</th>
                    <th className="font-medium text-muted-foreground w-[180px]">Generated Date</th>
                  </tr>
                </thead>
                <tbody>
                  {epcs.map((e) => (
                    <tr key={e.id} className="border-t">
                      <td className="py-2 w-[140px]">{e.sku}</td>
                      <td className="py-2 w-[160px]">{e.barcode}</td>
                      <td className="py-2 font-mono break-all">{e.epc}</td>
                      <td className="py-2 w-[180px]">{new Date(e.generatedAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {isCreateShippingOpen && (
        <CreateShipping
          handleSubmit={handleSubmitShipment}
          setIsModalOpen={setIsCreateShippingOpen}
          preSelectedPOId={activePurchaseOrderId}
        />
      )}
      {isCreateContainerOpen && (
        <CreateContainerByShipping
          handleSubmit={handleSubmitContainer}
          setIsContainerOpen={setIsCreateContainerOpen}
          preSelectedPOId={activePurchaseOrderId}
        />
      )}

      {isEmailComposerOpen && (
        <PurchaseOrderEmailComposer
          purchaseOrderId={activePurchaseOrderId}
          poNumber={po.poNumber}
          vendorEmail={po.vendorEmail || vendorInfo?.email}
          epcsGenerated={epcs.length > 0}
          onClose={() => setIsEmailComposerOpen(false)}
          onSuccess={() => {
            // Could show a success toast here if desired
          }}
        />
      )}
      <ConfrimModal
        isOpen={isSendConfirmOpen}
        isLoading={isActivating}
        title="Publish Purchase Order"
        description={activationError || "Once this PO is published, it will go live and be synced to ShipHero. Do you wish to continue?"}
        cancelButtonTitle="No"
        confirmButtonTitle="Yes, Publish"
        cancelClick={closeSendConfirmation}
        submitClick={handleSend}
        size="max-w-lg"
      >
        {cartonMismatches.length > 0 && (
          <div className="mb-5 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert">
            <p className="font-semibold">Carton quantity warning</p>
            <p className="mt-1">The following items do not match their carton quantities:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {cartonMismatches.map((mismatch) => (
                <li key={mismatch.sku}>
                  {mismatch.sku}: {mismatch.quantity} → {mismatch.floorQuantity} or {mismatch.ceilQuantity} (carton {mismatch.cartonQty})
                </li>
              ))}
            </ul>
          </div>
        )}
      </ConfrimModal>
    </div>
  );
}
